import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Input from '@/components/Inputs/Input';
import Typo from '@/components/Typography/Typo';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import InlineKeywordFilterV2 from '@/features/StructuredFilter/components/InlineKeywordFilterV2';
import useStructuredFilterQuery from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import StructuredFilter from '@/features/StructuredFilter/StructuredFilter';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import type { TOPIC_2_TYPE } from '@/types/taxonomyConst';

type Props = Topic2CreateParams<TOPIC_2_TYPE.RULE> & {
  alias: string;
  previewLoading?: boolean;
  onTitleChange: (name: string) => string;
  onRuleChange: (rule?: FilterRule) => void;
};

const RuleTopicEditItem = (props: Props) => {
  const init = useRef(false);
  const [filter, setFilter] = useState<StructuredFilter | undefined>(
    props.filter_rule,
  );
  const { parseStructuredFilter } = useStructuredFilter();
  const { filterQueryFormats, onFilterSetChange, clearFilter } =
    useStructuredFilterQuery({
      onFilterChangeCallback: (formats) => {
        // 기존 filter에서 keyword 필터 보존
        const currentFilter = new SFilter(filter);
        const includeKeywords = currentFilter.getIncludeTextFilter();
        const excludeKeywords = currentFilter.getExcludeTextFilter();

        const newFilter = new SFilter();
        newFilter.addFilterQueryFormats(formats);
        // keyword 필터 복원
        newFilter.setIncludeTextFilter(includeKeywords);
        newFilter.setExcludeTextFilter(excludeKeywords);

        setFilter(newFilter.get());
        props.onRuleChange(newFilter.get());
      },
    });

  useEffect(() => {
    if (init.current) return;
    const initialize = async () => {
      const dataFilters = new SFilter(filter).getDataFilterMap();
      const formats = parseStructuredFilter(dataFilters);
      onFilterSetChange(formats);
    };
    initialize();
    init.current = true;
  }, [filter, onFilterSetChange, parseStructuredFilter, props.filter_rule]);

  const onIncludeKeywordChange = (
    newKeywordFilter: StructuredFilterStringNumberValueSet | null,
  ) => {
    const newFilter = new SFilter(filter);
    newFilter.setIncludeTextFilter(newKeywordFilter);
    setFilter(newFilter.get());
    props.onRuleChange(newFilter.get());
  };

  const onExcludeKeywordChange = (
    newKeywordFilter: StructuredFilterStringNumberValueSet | null,
  ) => {
    const newFilter = new SFilter(filter);
    newFilter.setExcludeTextFilter(newKeywordFilter);
    setFilter(newFilter.get());
    props.onRuleChange(newFilter.get());
  };

  const filterIncludeTextFilter = useMemo(() => {
    return new SFilter(filter).getIncludeTextFilter();
  }, [JSON.stringify(filter)]);

  const filterExcludeTextFilter = useMemo(() => {
    return new SFilter(filter).getExcludeTextFilter();
  }, [JSON.stringify(filter)]);

  return (
    <Style>
      <VFlex g={44}>
        <Flex j="space-between" a="flex-end">
          <Input
            sizeType="l"
            css={inputCss}
            defaultValue={props.name}
            placeholder="type topic name"
            onBlur={(e) => {
              const newValue = props.onTitleChange(e.target.value);
              e.target.value = newValue;
            }}
          />
        </Flex>
        <VFlex g={24}>
          <SFilterRow>
            <Flex j="space-between" a="center" style={{ width: '100%' }}>
              <SLabelText type="label2" weight="sb">
                Filter
              </SLabelText>
              <SLabelText>Match with all filter</SLabelText>
            </Flex>
          </SFilterRow>
          <VFlex g={16}>
            <SFilterRow>
              <SLabelText>Feedback filter</SLabelText>
              <StructuredFilter
                disAllowFilter={[FEEDBACK_COLUMNS.TAXONOMY_TOPIC]}
                values={filterQueryFormats}
                onChange={onFilterSetChange}
                clearFilter={clearFilter}
              />
            </SFilterRow>
            <SFilterRow>
              <SLabelText>Include keywords</SLabelText>
              <InlineKeywordFilterV2
                name={`topic-include-keyword-filter-${props.alias}`}
                keywordFilter={filterIncludeTextFilter}
                onChange={onIncludeKeywordChange}
                allowSpace
                restriction="include"
              />
            </SFilterRow>
            <SFilterRow>
              <SLabelText>Exclude keywords</SLabelText>
              <InlineKeywordFilterV2
                name={`topic-exclude-keyword-filter-${props.alias}`}
                keywordFilter={filterExcludeTextFilter}
                onChange={onExcludeKeywordChange}
                allowSpace
                restriction="exclude"
              />
            </SFilterRow>
          </VFlex>
        </VFlex>
      </VFlex>
    </Style>
  );
};

const Style = styled(VFlex)`
  padding: 24px;
  background-color: ${(props) => props.theme.colors.gray200};
  border-radius: ${(props) => props.theme.radius.card}px;
`;

const SLabelText = styled(Typo)`
  color: ${(props) => props.theme.colors.gray600};
  flex-shrink: 0;
`;

const SFilterRow = styled(VFlex)`
  align-items: flex-start;
  gap: 8px;
`;

const inputCss = css`
  background-color: ${(props) => props.theme.colors.gray100};
`;

export default RuleTopicEditItem;
